/* eslint-disable no-shadow, no-return-assign, no-param-reassign */
import { isDebugModeEnabled } from 'es6/controllers/apps/debug-overrides';
import SentenceBuilderRecall from 'es6/components/apps/screens/sentence-builder-recall';
import SentenceBuilderQuiz from 'es6/components/apps/screens/sentence-builder-quiz';
import SentenceBuilderConfirmation from 'es6/components/apps/screens/sentence-builder-confirmation';
import SentenceQuizBase from 'es6/controllers/apps/quizzes/sentence-quiz-base';
import WordChoice from 'es6/models/word-choice';
import WordSlot from 'es6/models/word-slot';

/**
 * Base class for the listening and translation assembly quizzes.
 */
export class SentenceStudyAssemblyBase extends SentenceQuizBase {
  constructor(...args) {
    super(...args);

    // Define in subclass class.
    this._mode = null;

    // Keep track of hints and mistakes.
    this._hints = 0;
    this._mistakes = 0;

    // Build distractors, word slots and word choices.
    const wordEntities = this._sentence.wordEntities;

    // Build word choices and word slots.
    this._wordChoices = [];
    this._wordSlots = [];

    wordEntities.forEach((wordEntity, i) => {
      const wordChoice = new WordChoice(wordEntity);
      const wordSlot = new WordSlot({ expected: wordEntity.value });

      // If it is punctuation, fill the word slot and don't
      // add to possible choices.
      if (wordEntity.isPunctuation) {
        wordSlot.insert(wordChoice, true);
        wordSlot.isPunctuation = true;
      } else {
        this._wordChoices.push(wordChoice);
      }

      this._wordSlots.push(wordSlot);
    });

    // Add distractors and shuffle the word choices.
    const distractors = this._sentence.distractors.map(d => new WordChoice({ value: d }));
    this._wordChoices = this._wordChoices.concat(distractors);
    this._wordChoices = _.shuffle(this._wordChoices);
  }

  /**
   * Fills the fields with the correct answers. Used by debug mode.
   */
  _fillFieldsWithCorrectAnswers() {
    // Open up incorrect slots.
    this._wordSlots.filter(slot => slot.isIncorrect()).forEach(slot => slot.free());

    // Populate with appropriate word choices.
    this._wordSlots.filter(slot => !slot.isFilled()).forEach(slot => {
      const wordChoice = this._wordChoices.find(wc => !wc.used && wc.value === slot.expected);
      slot.insert(wordChoice);
    });
  }

  /**
   * Updates meta data and finishes the quiz.
   *
   * @override
   */
  finishQuiz() {
    super.finishQuiz();
  }

  /**
   * Callback for when a choice is clicked. There are two possibilities:
   * either a word choice inside a slot was clicked or a word choice
   * from the word bank was clicked.
   *
   * @param {WordChoice} wordChoice
   */
  _handleClick(wordChoice) {
    if (wordChoice.used) {
      // If the word choice is used, find the word slot and free the choice.
      const wordSlot = WordSlot.findWordSlotByWordChoice(this._wordSlots, wordChoice);

      if (!wordSlot.isFrozen()) {
        wordSlot.free();
      }
    } else {
      // Find the first empty slot and fill it.
      const wordSlot = this._wordSlots.find(wordSlot => !wordSlot.isFilled() || wordSlot.isIncorrectAndGraded());

      if (wordSlot) {
        wordSlot.insert(wordChoice);
      }
    }

    this._showQuizScreen();
  }

  /**
   * Callback for when word choice is dragged.
   *
   * @param {WordChoice} wordChoice
   * @param {Event} e
   */
  _handleDrag(wordChoice, e) {
    if (!wordChoice) {
      return;
    }

    const wordSlot = WordSlot.findWordSlotByWordChoice(this._wordSlots, wordChoice);

    if (wordSlot) {
      wordSlot.free();
    }

    this._showQuizScreen();
  }

  /**
   * Callback for when a drag event end. If the word choice is
   * in a word slot, clear the word slot.
   *
   * @param {WordChoice} wordChoice
   * @param {Event} e
   */
  _handleDragEnd(wordChoice, e) {
    if (e.dataTransfer.dropEffect === 'none') {
      const wordSlot = WordSlot.findWordSlotByWordChoice(this._wordSlots, wordChoice);

      if (wordSlot) {
        wordSlot.free();
      }
    }

    this._showQuizScreen();
  }

  /**
   * Callback for when a word choice is dropped on a slot. There
   * are a few important things to consider.
   *
   * 1) If toWordSlot is falsey, this means that the word choice is being
   *    returned to the original drop zone.
   * 2) The word choice might be occupying an existing word slot.
   * 3) The slot the word choice is beinged moved to might contain a word choice.
   *
   * @param {WordSlot} toWordSlot      the slot being dropped into
   * @param {WordChoice} wordChoice    the word choice used to drop into
   */
  _handleDrop(toWordSlot, wordChoice) {
    const fromWordSlot = WordSlot.findWordSlotByWordChoice(this._wordSlots, wordChoice);

    // If the word choice is coming from another word slot, free it.
    if (fromWordSlot) {
      fromWordSlot.free();
    } else {
      wordChoice.free();
    }

    if (toWordSlot) {
      // If the new word slot has a word choice in it. Remove it.
      const swapChoice = toWordSlot.free();

      // If there was a choice and the choice is coming from another slot.
      // Perform a switch.
      if (swapChoice && fromWordSlot) {
        fromWordSlot.insert(swapChoice);
      }

      toWordSlot.insert(wordChoice);
    }

    this._showQuizScreen();
  }

  /**
   * Callback for when the hint button is used.
   */
  _handleHint() {
    if (this._getMaxHints() === this._hints) {
      return;
    }

    const wordSlot = this._wordSlots.find(wordSlot => wordSlot.isIncorrectAndUnhinted());

    // If there is a word slot, free the slot and set the hinted flag to true.
    if (wordSlot) {
      wordSlot.free();
      wordSlot.hinted = true;
      this._hints++;
    }

    this.updateQuizData();
    this._showQuizScreen();
  }

  /**
   * If debugging is on, populate the quiz with the correct answers.
   *
   * @override
   */
  _handleRecallNext(...args) {
    if (isDebugModeEnabled()) {
      this._fillFieldsWithCorrectAnswers();
    }

    super._handleRecallNext(...args);
  }

  /**
   * Callback for when a slot in a quiz is shifted.
   *
   * @param {Number} slotIndex         the index of the slot being pushed
   * @param {WordChoice} wordChoice    the word choice to push in
   */
  _handleShift(slotIndex, wordChoice) {
    WordSlot.shiftWordSlots(this._wordSlots, slotIndex);
    this._wordSlots[slotIndex].insert(wordChoice);
    this._showQuizScreen();
  }

  /**
   * Callback for submitting the sentence trainer quiz.
   */
  _handleSubmit() {
    // Mark all the slots as graded.
    this._wordSlots.forEach(wordSlot => wordSlot.graded = true);

    // See if there are any incorrect.
    const incorrect = this._wordSlots.some(wordSlot => wordSlot.isIncorrect());

    // Increment mistakes if there are.
    if (incorrect) {
      this._mistakes++;
    } else {
      this._isCorrect = true;
    }

    // Is the quiz over?
    const endQuiz = this._isCorrect || this._mistakes === this._getMaxMistakes();

    this._setQuizResult();

    if (endQuiz) {
      this.result.beginPhase('confirmation');
      this._showConfirmationScreen();
    } else {
      // Create a new quiz result.
      this._pushQuizResult();
      this._showQuizScreen();
    }
  }

  /**
   * Along with setting the result, add the meta data.
   *
   * @override
   */
  _setQuizResult() {
    const enteredText = this._wordSlots.map(w => w.value).join(' ');
    this.updateQuizData({ enteredText, hintsUsed: this._hints });
    super._setQuizResult();
  }

  show() {
    this.result.beginPhase('recall');
    this._showRecallScreen();
  }

  /**
   * Renders the confirmation screen.
   *
   * @override
   */
  _showConfirmationScreen() {
    const props = {
      isCorrect:           this._isCorrect,
      maxMistakes:         this._getMaxMistakes(),
      mistakes:            this._mistakes,
      onNext:              this.finishQuiz.bind(this),
      wordSlots:           this._wordSlots,
      sentence:            this._sentence,
    };

    this._screenManager.renderComponent(SentenceBuilderConfirmation, props);
  }

  /**
   * Renders or rerenders the quiz screen.
   *
   * @override
   */
  _showQuizScreen() {
    const props = {
      hints:       this._hints,
      maxHints:    this._getMaxHints(),
      maxMistakes: this._getMaxMistakes(),
      mistakes:    this._mistakes,
      mode:        this._mode,
      onClick:     this._handleClick.bind(this),
      onDrag:      this._handleDrag.bind(this),
      onDragEnd:   this._handleDragEnd.bind(this),
      onDrop:      this._handleDrop.bind(this),
      onDontKnow:  this._handleDontKnow.bind(this),
      onHint:      this._handleHint.bind(this),
      onShift:     this._handleShift.bind(this),
      onSubmit:    this._handleSubmit.bind(this),
      sentence:    this._sentence,
      wordChoices: this._wordChoices,
      wordSlots:   this._wordSlots,
    };

    this._screenManager.renderComponent(SentenceBuilderQuiz, props);
  }

  /**
   * Renders the recall screen.
   *
   * @override
   */
  _showRecallScreen() {
    const props = {
      mode:       this._mode,
      onDontKnow: this._handleDontKnow.bind(this),
      onNext:     this._handleRecallNext.bind(this),
      sentence:   this._sentence,
    };

    this._screenManager.renderComponent(SentenceBuilderRecall, props);
  }
}

export default SentenceStudyAssemblyBase;
